import { format } from 'date-fns/format';
import { useTranslation } from 'react-i18next';

import NameWithCode from '@features/campaigns/shared/components/NameWithCode/NameWithCode';
import { SectionWrapper } from '@features/campaigns/shared/components/Review/SectionWrapper/SectionWrapper';
import { CampaignDetailsData } from '@features/campaigns/shared/containers/Review/Review.dto';
import { ReviewSectionProps } from '@features/campaigns/shared/containers/Review/types';
import { useIsBuilderView } from '@features/campaigns/shared/hooks/useIsBuilderView';
import { formatCampaignCountryName } from '@features/campaigns/shared/utils/formatCampaignCountryName.ts';
import { AccessRights } from '@shared/api/commonApiInterfaces.ts';
import { Item } from '@shared/components/Review/Item/Item';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { useUserDetails } from '@shared/providers/UserDetailsProvider/useUserDetails.tsx';
import { formatNumberAsMoney } from '@shared/utils/moneyFormat.ts';

export function CampaignDetails({
  headerText,
  ctaButtonCallback,
  data,
  accessRights,
}: ReviewSectionProps<CampaignDetailsData>) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const { isSuperAdminUser } = useUserDetails();
  const formattedBudget = formatNumberAsMoney(data.budget);
  const formattedStartDate = format(new Date(data.startDate), 'dd/MM/yyyy');
  const formattedEndDate = format(new Date(data.endDate), 'dd/MM/yyyy');
  const formattedDateRange = `${formattedStartDate} - ${formattedEndDate}`;
  const formattedAgencyCommission = data.isAgencyCommissionGross
    ? t('builder.formFields.agencyCommission.gross')
    : t('builder.formFields.agencyCommission.net');
  const formattedCountryName = t(formatCampaignCountryName(data.country), { ns: i18nNameSpace.COMMON });
  const isBuilderView = useIsBuilderView();

  const canReadAdvertiserAndBrandCode = Boolean(accessRights?.includes(AccessRights.READ_ADVERTISER_BRAND_CODE));

  return (
    <SectionWrapper headerText={headerText} ctaButtonCallback={ctaButtonCallback}>
      <div className={'grid'} data-testid='campaign-details-section'>
        <div className={'row'}>
          <div className={'col-6'}>
            <Item
              label={t('builder.formFields.advertiser.label')}
              value={
                <NameWithCode
                  name={data.advertiserName}
                  code={data.advertiserCode}
                  showCode={canReadAdvertiserAndBrandCode}
                />
              }
            />
          </div>
          <div className={'col-6'}>
            <Item
              label={t('builder.formFields.brand.label')}
              value={
                <NameWithCode name={data.brandName} code={data.brandCode} showCode={canReadAdvertiserAndBrandCode} />
              }
            />
          </div>
        </div>
        <div className={'row'}>
          <div className={'col-12'}>
            <Item label={t('builder.formFields.daterange.submitSectionLabel')} value={formattedDateRange} />
          </div>
        </div>
        <div className={'row'}>
          <div className={'col-6'}>
            <Item label={t('builder.formFields.budget.label')} value={formattedBudget} />
          </div>
          <div className={'col-6'}>
            <Item label={t('builder.formFields.agencyCommission.label')} value={formattedAgencyCommission} />
          </div>
        </div>
        {isBuilderView && (
          <div className={'row'}>
            <div className={'col-6'}>
              <Item label={t('builder.formFields.campaignName.label')} value={data.campaignName} />
            </div>
            {isSuperAdminUser && (
              <div className={'col-6'}>
                <Item label={t('builder.formFields.country.label')} value={formattedCountryName} />
              </div>
            )}
          </div>
        )}
        {!isBuilderView && isSuperAdminUser && (
          <div className={'row'}>
            <div className={'col-12'}>
              <Item label={t('builder.formFields.country.label')} value={formattedCountryName} />
            </div>
          </div>
        )}
      </div>
    </SectionWrapper>
  );
}
